import React, { useState } from "react";
import { Box, Typography, Chip, IconButton, CircularProgress, Tooltip } from '@mui/material';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import LabelHtmlToTextCustom from "../../../../components/LabelHtmlToTextCustom/LabelHtmlToTextCustom";
import { downloadFile } from "../../../../services/FileService";
import { withSnackbar } from "../../../../utils/SnackbarHOC";

function ElegantFileCard({ file, snackbarShowMessage }) {
  const [loading, setLoading] = useState(false);
  const ext = file.imageName?.split('.').pop()?.toLowerCase();
  const isPdf = ext === 'pdf';
  const isDoc = ['doc', 'docx'].includes(ext);

  const handleDownload = async () => {
    const FileDownload = require("js-file-download");
    setLoading(true);
    try {
      const response = await downloadFile(file.id);
      FileDownload(response.data, file.imageName);
    } catch (e) {
      snackbarShowMessage?.(e.message || "Impossible de télécharger.", "error", 4000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.5, py: 1.1,
        borderRadius: '9px',
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        mb: 0.75,
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: '#aed6f1',
          backgroundColor: '#f0f7fd',
          boxShadow: '0 2px 8px rgba(99,102,241,0.1)',
        },
      }}
    >
      <Box sx={{
        width: 38, height: 38, borderRadius: '8px', flexShrink: 0,
        backgroundColor: isPdf ? '#fee2e2' : isDoc ? '#dbeafe' : '#f3f4f6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <DescriptionOutlined sx={{ fontSize: 20, color: isPdf ? '#ef4444' : isDoc ? '#3b82f6' : '#6b7280' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.imageName}
        </Typography>
        <Typography sx={{ fontSize: '0.64rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.1 }}>
          {ext || 'fichier'}
        </Typography>
      </Box>
      <Tooltip title="Télécharger" placement="top" arrow>
        <IconButton
          size="small"
          onClick={handleDownload}
          disabled={loading}
          sx={{
            backgroundColor: '#ebf4fb', color: '#03407b',
            width: 32, height: 32, flexShrink: 0,
            '&:hover': { backgroundColor: '#03407b', color: '#fff' },
            '&.Mui-disabled': { backgroundColor: '#f3f4f6', color: '#d1d5db' },
            transition: 'all 0.15s ease',
          }}
        >
          {loading
            ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
            : <DownloadOutlined sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function TextSection({ title, content }) {
  const isEmpty = !content || content.trim() === '' || content === 'null';
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Box sx={{ width: 3, height: 13, borderRadius: 2, background: 'linear-gradient(180deg, #03407b, #02357a)', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#03407b', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{
        px: 1.75, py: 1.25, borderRadius: '8px',
        backgroundColor: isEmpty ? '#f9fafb' : '#fafafe',
        border: `1px solid ${isEmpty ? '#f3f4f6' : '#e5e7eb'}`,
        minHeight: 42,
      }}>
        {isEmpty ? (
          <Typography sx={{ fontSize: '1rem', color: '#d1d5db', fontStyle: 'italic' }}>—</Typography>
        ) : (
          <Box sx={{ fontSize: '0.87rem', color: '#374151', lineHeight: 1.65 }}>
            {content.split('\n').map((item, i) => (
              <LabelHtmlToTextCustom key={i + item} data={item} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function FileGroup({ title, files, snackbarShowMessage }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Box sx={{ width: 3, height: 13, borderRadius: 2, background: 'linear-gradient(180deg, #03407b, #02357a)', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#03407b', letterSpacing: '0.1em', textTransform: 'uppercase', flex: 1 }}>
          {title}
        </Typography>
        {files.length > 0 && (
          <Chip
            label={files.length}
            size="small"
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, backgroundColor: '#ebf4fb', color: '#02357a', border: '1px solid #aed6f1', '& .MuiChip-label': { px: 0.75 } }}
          />
        )}
      </Box>
      {files.length === 0 ? (
        <Box sx={{ px: 1.5, py: 1, borderRadius: '8px', backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '1rem', color: '#d1d5db', fontStyle: 'italic' }}>Aucun fichier</Typography>
        </Box>
      ) : (
        files.map(file => (
          <ElegantFileCard key={file.id} file={file} snackbarShowMessage={snackbarShowMessage} />
        ))
      )}
    </Box>
  );
}

function SyntheseConsultant({ commentaire, softskills, pointsforts, uploadedFiles, snackbarShowMessage }) {
  const cvs = uploadedFiles ? uploadedFiles.filter(f => f.categorie === "cv") : [];
  const cvsFormates = uploadedFiles ? uploadedFiles.filter(f => f.categorie === "cvFormate") : [];
  const autresPieces = uploadedFiles ? uploadedFiles.filter(f => f.categorie === "autrePiece") : [];

  return (
    <Box>
      <TextSection title="Points Forts" content={pointsforts} />
      <TextSection title="Soft Skills" content={softskills} />
      <TextSection title="Commentaires" content={commentaire} />

      <Box sx={{ mt: 0.5, pt: 2, borderTop: '1px solid #f3f4f6' }}>
        <FileGroup title="CV" files={cvs} snackbarShowMessage={snackbarShowMessage} />
        <FileGroup title="CV Formaté" files={cvsFormates} snackbarShowMessage={snackbarShowMessage} />
        <FileGroup title="Retour testeur" files={autresPieces} snackbarShowMessage={snackbarShowMessage} />
      </Box>
    </Box>
  );
}

export default withSnackbar(SyntheseConsultant);
